/**
 * PsicoSalud — Guía de uso en ventana modal.
 *
 * Aparece sola la primera vez que se entra al panel, y después queda
 * accesible desde el botón de la barra superior. El resumen es corto a
 * propósito: seis pasos para saber moverse por el sistema, no el
 * manual completo, que sigue siendo el material de la tesis.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/** Clave en localStorage. La `v1` permite cambiar el contenido después. */
const CLAVE = 'psicosalud.guia.v1';
const PASOS = 6;

export function guiaVista(): boolean {
  try {
    return window.localStorage.getItem(CLAVE) === '1';
  } catch {
    // Modo privado o almacenamiento bloqueado: se trata como no vista para
    // que la guía aparezca. Es preferible mostrarla de más que dejarla
    // escondida detrás de un botón que nadie encuentra.
    return false;
  }
}

export function marcarGuiaVista(): void {
  try {
    window.localStorage.setItem(CLAVE, '1');
  } catch {
    // Sin almacenamiento la guía vuelve a salir la próxima vez. No es grave.
  }
}

export function reiniciarGuia(): void {
  try {
    window.localStorage.removeItem(CLAVE);
  } catch {
    // idem
  }
}

interface GuideModalProps {
  abierto: boolean;
  onClose: () => void;
  /** Primera apertura: el botón de cerrar dice "entendido" y no hay cerrar con Escape. */
  primeraVez?: boolean;
}

export default function GuideModal({
  abierto,
  onClose,
  primeraVez = false,
}: GuideModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const botonRef = useRef<HTMLButtonElement>(null);

  // Escape cierra y devuelve el foco al botón que la abrió, para que quien
  // navega con teclado no quede perdido dentro de la página.
  useEffect(() => {
    if (!abierto) return undefined;
    const alPulsar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [abierto, onClose]);

  // Al abrir, el foco entra al panel: el lector de pantalla anuncia el
  // diálogo y el teclado no queda en la página de atrás.
  useEffect(() => {
    if (abierto) panelRef.current?.focus();
  }, [abierto]);

  if (!abierto) return null;

  return (
    <div
      className="modal-overlay"
      data-cy="guide-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-title"
        data-cy="guide-modal"
        ref={panelRef}
        tabIndex={-1}
        onClick={(evento) => evento.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="page-eyebrow">{t('guide.eyebrow')}</span>
            <h2 id="guide-title">{t('guide.title')}</h2>
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label={t('guide.close')}
            data-cy="guide-close-x"
          >
            ×
          </button>
        </header>

        <p className="modal-intro">{t('guide.intro')}</p>

        <ol className="guide-steps">
          {Array.from({ length: PASOS }, (_, indice) => (
            <li key={indice} data-cy={`guide-step-${indice + 1}`}>
              <h3>{t(`guide.step${indice + 1}.title`)}</h3>
              <p>{t(`guide.step${indice + 1}.body`)}</p>
            </li>
          ))}
        </ol>

        <footer className="modal-footer">
          <p className="t-pequeno t-suave">{t('guide.footer')}</p>
          <button ref={botonRef} onClick={onClose} data-cy="guide-close">
            {primeraVez ? t('guide.gotIt') : t('guide.close')}
          </button>
        </footer>
      </div>
    </div>
  );
}
